import { useState, useEffect, useCallback } from 'react'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip,
  Avatar,
  Tab,
  Tabs,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import {
  Search,
  Download,
  Upload,
  MoreVert,
  Visibility,
  Edit,
  Delete,
  Explore,
  Public,
  Lock,
  FilterList,
  Refresh,
  Close,
  PlayArrow,
  CheckCircle,
  Cancel,
  PauseCircle,
  Person,
  ArrowBack,
  LocationOn,
  Timer,
  Lightbulb,
  Group,
  Warning,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '@api/endpoints/admin'
import {
  AdminAdventureDto,
  AdminAdventuresListParams,
  AdminAdventureStats,
  AdminAdventureDeleteResult,
  AdminSessionDto,
  AdminSessionDetailDto,
  AdminSessionsListParams,
  SessionStatus,
  SessionLocationLogDto,
  ADVENTURE_STATUS_MAP,
  MODERATION_STATUS_MAP,
} from '@api/types/admin'
import { CategoryListTable } from '../components/categories'
import { AdventureImportDialog } from '../components/adventures/AdventureImportDialog'
import { useAdventureCategories } from '../hooks/useAdventureCategories'

type SortField = 'Title' | 'CreatedDate' | 'UpdatedDate' | 'Status' | 'ParticipantCount' | 'CreatorUsername'

// Status: 0=Draft, 1=Testing, 2=Published, 3=Archived
const getStatusColor = (status: number) => {
  switch (status) {
    case 2: // Published
      return 'success'
    case 0: // Draft
      return 'default'
    case 1: // Testing
      return 'info'
    case 3: // Archived
      return 'warning'
    default:
      return 'default'
  }
}

// ModerationStatus: 0=Pending, 1=Approved, 2=Rejected, 3=Flagged
const getModerationStatusColor = (status: number) => {
  switch (status) {
    case 1: // Approved
      return 'success'
    case 0: // Pending
      return 'warning'
    case 2: // Rejected
      return 'error'
    case 3: // Flagged
      return 'error'
    default:
      return 'default'
  }
}

const getSessionStatusColor = (status: SessionStatus) => {
  switch (status) {
    case 'InProgress':
      return 'info'
    case 'Completed':
      return 'success'
    case 'Failed':
      return 'error'
    case 'Abandoned':
      return 'warning'
    default:
      return 'default'
  }
}

const getSessionStatusIcon = (status: SessionStatus) => {
  switch (status) {
    case 'InProgress':
      return <PlayArrow fontSize="small" />
    case 'Completed':
      return <CheckCircle fontSize="small" />
    case 'Failed':
      return <Cancel fontSize="small" />
    case 'Abandoned':
      return <PauseCircle fontSize="small" />
    default:
      return null
  }
}

const SESSIONS_DRAWER_WIDTH = 600

export default function AdminAdventuresPage() {
  usePageTitle('Adventures')
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(0)
  const [adventures, setAdventures] = useState<AdminAdventureDto[]>([])
  const [stats, setStats] = useState<AdminAdventureStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState<string | null>(null)

  // Fetch categories for dynamic category name lookup
  const { data: categories } = useAdventureCategories(true)

  // Pagination
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)

  // Sorting
  const [sortBy, setSortBy] = useState<SortField>('CreatedDate')
  const [sortDescending, setSortDescending] = useState(true)

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<number | ''>('')
  const [moderationFilter, setModerationFilter] = useState<number | ''>('')
  const [showFilters, setShowFilters] = useState(false)

  // Menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedAdventure, setSelectedAdventure] = useState<AdminAdventureDto | null>(null)

  // Sessions drawer
  const [sessionsDrawerOpen, setSessionsDrawerOpen] = useState(false)
  const [sessionsAdventure, setSessionsAdventure] = useState<AdminAdventureDto | null>(null)
  const [sessions, setSessions] = useState<AdminSessionDto[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsError, setSessionsError] = useState<string | null>(null)
  const [sessionsPage, setSessionsPage] = useState(0)
  const [sessionsPageSize, setSessionsPageSize] = useState(25)
  const [sessionsTotalCount, setSessionsTotalCount] = useState(0)
  const [sessionsUserSearch, setSessionsUserSearch] = useState('')
  const [sessionsStatusFilter, setSessionsStatusFilter] = useState<SessionStatus | ''>('')

  // Session detail
  const [selectedSession, setSelectedSession] = useState<AdminSessionDetailDto | null>(null)
  const [sessionLocations, setSessionLocations] = useState<SessionLocationLogDto[]>([])
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false)
  const [sessionDetailError, setSessionDetailError] = useState<string | null>(null)

  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false)

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [adventureToDelete, setAdventureToDelete] = useState<AdminAdventureDto | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteResult, setDeleteResult] = useState<AdminAdventureDeleteResult | null>(null)

  const fetchAdventures = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: AdminAdventuresListParams = {
        page: page + 1, // API is 1-based
        pageSize,
        sortBy,
        sortDescending,
        search: search || undefined,
        status: statusFilter !== '' ? statusFilter : undefined,
        moderationStatus: moderationFilter !== '' ? moderationFilter : undefined,
      }
      const response = await adminApi.getAdventures(params)
      setAdventures(response.items)
      setTotalCount(response.totalCount)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch adventures')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, sortBy, sortDescending, search, statusFilter, moderationFilter])

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminApi.getAdventureStats()
      setStats(response)
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    }
  }, [])

  useEffect(() => {
    fetchAdventures()
  }, [fetchAdventures])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDescending(!sortDescending)
    } else {
      setSortBy(field)
      setSortDescending(true)
    }
    setPage(0)
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, adventure: AdminAdventureDto) => {
    setAnchorEl(event.currentTarget)
    setSelectedAdventure(adventure)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedAdventure(null)
  }

  const handleExport = async (adventure: AdminAdventureDto) => {
    setExporting(adventure.id)
    handleMenuClose()
    try {
      await adminApi.downloadAdventureExport(
        adventure.id,
        `${adventure.title.replace(/[^a-z0-9]/gi, '_')}-export.zip`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export adventure')
    } finally {
      setExporting(null)
    }
  }

  const handleViewAdventure = (adventure: AdminAdventureDto) => {
    handleMenuClose()
    navigate(`/adventures/${adventure.id}`)
  }

  const handleEditAdventure = (adventure: AdminAdventureDto) => {
    handleMenuClose()
    navigate(`/adventures/${adventure.id}/edit`)
  }

  const handleDeleteClick = (adventure: AdminAdventureDto) => {
    handleMenuClose()
    setAdventureToDelete(adventure)
    setDeleteResult(null)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!adventureToDelete) return

    setDeleting(true)
    try {
      const result = await adminApi.deleteAdventure(adventureToDelete.id)
      setDeleteResult(result)
      // Refresh the list after successful deletion
      fetchAdventures()
      fetchStats()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete adventure')
      setDeleteDialogOpen(false)
      setAdventureToDelete(null)
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false)
    setAdventureToDelete(null)
    setDeleteResult(null)
  }

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      setPage(0)
      fetchAdventures()
    }
  }

  // Sessions drawer handlers
  const fetchSessions = useCallback(async (adventureId: string) => {
    setSessionsLoading(true)
    setSessionsError(null)
    try {
      const params: AdminSessionsListParams = {
        adventureId,
        page: sessionsPage + 1,
        pageSize: sessionsPageSize,
        userId: sessionsUserSearch || undefined,
        status: sessionsStatusFilter || undefined,
        sortBy: 'CreatedAt',
        sortDescending: true,
      }
      const response = await adminApi.getSessions(params)
      setSessions(response.items || [])
      setSessionsTotalCount(response.totalCount || 0)
    } catch (err) {
      setSessionsError(err instanceof Error ? err.message : 'Failed to fetch sessions')
    } finally {
      setSessionsLoading(false)
    }
  }, [sessionsPage, sessionsPageSize, sessionsUserSearch, sessionsStatusFilter])

  const handleOpenSessionsDrawer = (adventure: AdminAdventureDto) => {
    setSessionsAdventure(adventure)
    setSessionsDrawerOpen(true)
    setSessionsPage(0)
    setSessionsUserSearch('')
    setSessionsStatusFilter('')
    fetchSessions(adventure.id)
  }

  const handleCloseSessionsDrawer = () => {
    setSessionsDrawerOpen(false)
    setSessionsAdventure(null)
    setSessions([])
    setSessionsError(null)
    setSelectedSession(null)
    setSessionLocations([])
    setSessionDetailError(null)
  }

  const handleSelectSession = async (session: AdminSessionDto) => {
    setSessionDetailLoading(true)
    setSessionDetailError(null)
    try {
      const [detail, locations] = await Promise.all([
        adminApi.getSession(session.id),
        adminApi.getSessionLocations(session.id),
      ])
      setSelectedSession(detail)
      setSessionLocations(locations)
    } catch (err) {
      setSessionDetailError(err instanceof Error ? err.message : 'Failed to fetch session details')
    } finally {
      setSessionDetailLoading(false)
    }
  }

  const handleBackToSessionsList = () => {
    setSelectedSession(null)
    setSessionLocations([])
    setSessionDetailError(null)
  }

  // Refetch sessions when filters change
  useEffect(() => {
    if (sessionsAdventure) {
      fetchSessions(sessionsAdventure.id)
    }
  }, [sessionsPage, sessionsPageSize, sessionsUserSearch, sessionsStatusFilter, sessionsAdventure, fetchSessions])

  const handleSessionsSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      setSessionsPage(0)
    }
  }

  const formatSessionDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString()
  }

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Adventure Management" />
        <Tab label="Category Management" />
      </Tabs>

      {activeTab === 0 && (
        <>
          {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold">
                  {stats.totalCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {stats.publishedCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Published
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold" color="text.secondary">
                  {stats.draftCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Draft
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {stats.testingCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Testing
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {stats.pendingModerationCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pending Review
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {stats.archivedCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Archived
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by title or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            size="small"
            sx={{ minWidth: 300, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant={showFilters ? 'contained' : 'outlined'}
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => setImportDialogOpen(true)}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchAdventures()
              fetchStats()
            }}
          >
            Refresh
          </Button>
        </Box>

        {showFilters && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  const val = e.target.value
                  setStatusFilter(val === '' ? '' : Number(val))
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={0}>Draft</MenuItem>
                <MenuItem value={1}>Testing</MenuItem>
                <MenuItem value={2}>Published</MenuItem>
                <MenuItem value={3}>Archived</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Moderation</InputLabel>
              <Select
                value={moderationFilter}
                label="Moderation"
                onChange={(e) => {
                  const val = e.target.value
                  setModerationFilter(val === '' ? '' : Number(val))
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={0}>Pending</MenuItem>
                <MenuItem value={1}>Approved</MenuItem>
                <MenuItem value={2}>Rejected</MenuItem>
                <MenuItem value={3}>Flagged</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="text"
              onClick={() => {
                setSearch('')
                setStatusFilter('')
                setModerationFilter('')
                setPage(0)
              }}
            >
              Clear Filters
            </Button>
          </Box>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'Title'}
                    direction={sortBy === 'Title' ? (sortDescending ? 'desc' : 'asc') : 'asc'}
                    onClick={() => handleSort('Title')}
                    sx={{
                      color: 'white !important',
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    }}
                  >
                    Adventure
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'CreatorUsername'}
                    direction={sortBy === 'CreatorUsername' ? (sortDescending ? 'desc' : 'asc') : 'asc'}
                    onClick={() => handleSort('CreatorUsername')}
                    sx={{
                      color: 'white !important',
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    }}
                  >
                    Creator
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'Status'}
                    direction={sortBy === 'Status' ? (sortDescending ? 'desc' : 'asc') : 'asc'}
                    onClick={() => handleSort('Status')}
                    sx={{
                      color: 'white !important',
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Moderation</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'ParticipantCount'}
                    direction={sortBy === 'ParticipantCount' ? (sortDescending ? 'desc' : 'asc') : 'asc'}
                    onClick={() => handleSort('ParticipantCount')}
                    sx={{
                      color: 'white !important',
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    }}
                  >
                    Participants
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'CreatedDate'}
                    direction={sortBy === 'CreatedDate' ? (sortDescending ? 'desc' : 'asc') : 'asc'}
                    onClick={() => handleSort('CreatedDate')}
                    sx={{
                      color: 'white !important',
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    }}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : adventures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No adventures found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                adventures.map((adventure) => (
                  <TableRow
                    key={adventure.id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      cursor: 'pointer',
                    }}
                    onClick={() => handleOpenSessionsDrawer(adventure)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {adventure.thumbnailUrl ? (
                          <Avatar
                            src={adventure.thumbnailUrl}
                            variant="rounded"
                            sx={{ width: 40, height: 40 }}
                          >
                            <Explore />
                          </Avatar>
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'primary.light' }}>
                            <Explore />
                          </Avatar>
                        )}
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" fontWeight="bold">
                              {adventure.title}
                            </Typography>
                            <Tooltip title={adventure.isPublic ? 'Public' : 'Private'}>
                              {adventure.isPublic ? (
                                <Public fontSize="small" color="success" />
                              ) : (
                                <Lock fontSize="small" color="action" />
                              )}
                            </Tooltip>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Difficulty: {adventure.difficultyLevel}/5 | POIs: {adventure.poiCount}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {adventure.creatorDisplayName || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ADVENTURE_STATUS_MAP[adventure.status] || adventure.status}
                        color={getStatusColor(adventure.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={MODERATION_STATUS_MAP[adventure.moderationStatus] || adventure.moderationStatus}
                        color={getModerationStatusColor(adventure.moderationStatus) as any}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {categories?.find(c => c.id === adventure.categoryId)?.displayName || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{adventure.participantCount}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {adventure.completionCount} completed
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const formatDate = (dateStr?: string | null) => {
                          if (!dateStr) return 'N/A'
                          const date = new Date(dateStr)
                          return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString()
                        }
                        return (
                          <>
                            <Typography variant="body2">
                              {formatDate(adventure.createdDate)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Updated: {formatDate(adventure.updatedDate)}
                            </Typography>
                          </>
                        )
                      })()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Export">
                          <IconButton
                            size="small"
                            onClick={() => handleExport(adventure)}
                            disabled={exporting === adventure.id}
                          >
                            {exporting === adventure.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Download />
                            )}
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, adventure)}
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      </Paper>

          {/* Action Menu */}
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={() => selectedAdventure && handleViewAdventure(selectedAdventure)}>
              <Visibility sx={{ mr: 1 }} fontSize="small" />
              View
            </MenuItem>
            <MenuItem onClick={() => selectedAdventure && handleEditAdventure(selectedAdventure)}>
              <Edit sx={{ mr: 1 }} fontSize="small" />
              Edit
            </MenuItem>
            <MenuItem onClick={() => selectedAdventure && handleExport(selectedAdventure)}>
              <Download sx={{ mr: 1 }} fontSize="small" />
              Export
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => selectedAdventure && handleDeleteClick(selectedAdventure)}
              sx={{ color: 'error.main' }}
            >
              <Delete sx={{ mr: 1 }} fontSize="small" />
              Delete
            </MenuItem>
          </Menu>

          {/* Sessions Drawer */}
          <Drawer
            anchor="right"
            open={sessionsDrawerOpen}
            onClose={handleCloseSessionsDrawer}
            PaperProps={{ sx: { width: SESSIONS_DRAWER_WIDTH, maxWidth: '100vw' } }}
          >
            <Box sx={{ p: 2 }}>
              {selectedSession ? (
                /* Session Detail View */
                <>
                  {/* Header with Back Button */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton onClick={handleBackToSessionsList} size="small">
                        <ArrowBack />
                      </IconButton>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Session Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedSession.userDisplayName}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton onClick={handleCloseSessionsDrawer} size="small">
                      <Close />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {sessionDetailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : sessionDetailError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {sessionDetailError}
                    </Alert>
                  ) : (
                    <>
                      {/* Session Info Cards */}
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Chip
                              icon={getSessionStatusIcon(selectedSession.status) || undefined}
                              label={selectedSession.status}
                              color={getSessionStatusColor(selectedSession.status) as any}
                              size="small"
                            />
                            {selectedSession.isTestSession && (
                              <Chip label="Test" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                            )}
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              <Lightbulb color="warning" fontSize="small" />
                              <Typography variant="h6">{selectedSession.cluesUsed}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">Clues Used</Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      {/* User Info */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          <Person fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          User Information
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Display Name</Typography>
                            <Typography variant="body2">{selectedSession.userDisplayName}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Email</Typography>
                            <Typography variant="body2">{selectedSession.userEmail || 'N/A'}</Typography>
                          </Box>
                          {selectedSession.groupName && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Group</Typography>
                              <Typography variant="body2">
                                <Group fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                {selectedSession.groupName}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Paper>

                      {/* Timing Info */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          <Timer fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          Timeline
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Created</Typography>
                            <Typography variant="body2">{formatSessionDate(selectedSession.createdAt)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Started</Typography>
                            <Typography variant="body2">{formatSessionDate(selectedSession.startedAt)}</Typography>
                          </Box>
                          {selectedSession.completedAt && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Completed</Typography>
                              <Typography variant="body2" color="success.main">
                                {formatSessionDate(selectedSession.completedAt)}
                              </Typography>
                            </Box>
                          )}
                          {selectedSession.abandonedAt && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">Abandoned</Typography>
                              <Typography variant="body2" color="warning.main">
                                {formatSessionDate(selectedSession.abandonedAt)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Paper>

                      {/* Stats */}
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          Session Stats
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 1 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6">{selectedSession.nodeVisitCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Nodes Visited</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6">{selectedSession.locationLogCount}</Typography>
                            <Typography variant="caption" color="text.secondary">Location Logs</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6">{selectedSession.cluesUsed}</Typography>
                            <Typography variant="caption" color="text.secondary">Clues Used</Typography>
                          </Box>
                        </Box>
                      </Paper>

                      {/* Location Logs */}
                      {sessionLocations.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            Location History ({sessionLocations.length} points)
                          </Typography>
                          <TableContainer sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Coordinates</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Accuracy</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Distance</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {sessionLocations.map((loc) => (
                                  <TableRow key={loc.id} hover>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {new Date(loc.timestamp).toLocaleTimeString()}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {loc.latitude.toFixed(5)}, {loc.longitude.toFixed(5)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {loc.accuracy ? `${loc.accuracy.toFixed(0)}m` : 'N/A'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {loc.distanceFromPrevious ? `${loc.distanceFromPrevious.toFixed(0)}m` : '-'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {sessionLocations.length > 0 && sessionLocations[sessionLocations.length - 1].totalDistanceTraveled && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Total Distance: {(sessionLocations[sessionLocations.length - 1].totalDistanceTraveled! / 1000).toFixed(2)} km
                            </Typography>
                          )}
                        </Paper>
                      )}
                    </>
                  )}
                </>
              ) : (
                /* Sessions List View */
                <>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        Sessions
                      </Typography>
                      {sessionsAdventure && (
                        <Typography variant="body2" color="text.secondary">
                          {sessionsAdventure.title}
                        </Typography>
                      )}
                    </Box>
                    <IconButton onClick={handleCloseSessionsDrawer} size="small">
                      <Close />
                    </IconButton>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* Filters */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                      placeholder="Search by user..."
                      value={sessionsUserSearch}
                      onChange={(e) => setSessionsUserSearch(e.target.value)}
                      onKeyDown={handleSessionsSearchKeyDown}
                      size="small"
                      sx={{ flex: 1, minWidth: 200 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={sessionsStatusFilter}
                        label="Status"
                        onChange={(e) => {
                          setSessionsStatusFilter(e.target.value as SessionStatus | '')
                          setSessionsPage(0)
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="InProgress">In Progress</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                        <MenuItem value="Failed">Failed</MenuItem>
                        <MenuItem value="Abandoned">Abandoned</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Error Alert */}
                  {sessionsError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSessionsError(null)}>
                      {sessionsError}
                    </Alert>
                  )}

                  {/* Sessions Table */}
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Clues</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sessionsLoading ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                              <CircularProgress size={24} />
                            </TableCell>
                          </TableRow>
                        ) : sessions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No sessions found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          sessions.map((session) => (
                            <TableRow
                              key={session.id}
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleSelectSession(session)}
                            >
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {session.userDisplayName}
                                  </Typography>
                                  {session.userEmail && (
                                    <Typography variant="caption" color="text.secondary">
                                      {session.userEmail}
                                    </Typography>
                                  )}
                                  {session.groupName && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Group: {session.groupName}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={getSessionStatusIcon(session.status) || undefined}
                                  label={session.status}
                                  color={getSessionStatusColor(session.status) as any}
                                  size="small"
                                />
                                {session.isTestSession && (
                                  <Chip
                                    label="Test"
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 0.5 }}
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatSessionDate(session.createdAt)}
                                </Typography>
                                {session.completedAt && (
                                  <Typography variant="caption" color="success.main">
                                    Completed: {formatSessionDate(session.completedAt)}
                                  </Typography>
                                )}
                                {session.abandonedAt && (
                                  <Typography variant="caption" color="warning.main">
                                    Abandoned: {formatSessionDate(session.abandonedAt)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{session.cluesUsed}</Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={sessionsTotalCount}
                    rowsPerPage={sessionsPageSize}
                    page={sessionsPage}
                    onPageChange={(_, newPage) => setSessionsPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setSessionsPageSize(parseInt(e.target.value, 10))
                      setSessionsPage(0)
                    }}
                  />
                </>
              )}
            </Box>
          </Drawer>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteDialogClose}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {deleteResult ? (
                <>
                  <CheckCircle color="success" />
                  Adventure Deleted
                </>
              ) : (
                <>
                  <Warning color="error" />
                  Delete Adventure
                </>
              )}
            </DialogTitle>
            <DialogContent>
              {deleteResult ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {deleteResult.message}
                  </Alert>
                  <Typography variant="subtitle2" gutterBottom>
                    Deleted Items:
                  </Typography>
                  <List dense>
                    {deleteResult.deletedItems.sessionsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.sessionsDeleted} sessions`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.poisDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.poisDeleted} POIs`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.workflowVersionsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.workflowVersionsDeleted} workflow versions`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.requirementsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.requirementsDeleted} requirements`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.rewardsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.rewardsDeleted} rewards`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.tagsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.tagsDeleted} tags`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.ticketsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.ticketsDeleted} tickets`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.userProgressDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.userProgressDeleted} user progress records`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.discussionsDeleted > 0 && (
                      <ListItem>
                        <ListItemText primary={`${deleteResult.deletedItems.discussionsDeleted} discussions`} />
                      </ListItem>
                    )}
                    {deleteResult.deletedItems.mediaFilesDeleted > 0 && (
                      <ListItem>
                        <ListItemText
                          primary={`${deleteResult.deletedItems.mediaFilesDeleted} media files`}
                          secondary={`${formatBytes(deleteResult.deletedItems.mediaFileSizeBytesFreed)} freed`}
                        />
                      </ListItem>
                    )}
                  </List>
                  {deleteResult.deletedItems.mediaFileDeletionErrors.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Some media files could not be deleted:
                      </Typography>
                      {deleteResult.deletedItems.mediaFileDeletionErrors.map((error, i) => (
                        <Typography key={i} variant="body2">
                          {error}
                        </Typography>
                      ))}
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    This action cannot be undone. All data associated with this adventure will be permanently deleted.
                  </Alert>
                  {adventureToDelete && (
                    <Box>
                      <Typography variant="body1" gutterBottom>
                        Are you sure you want to delete <strong>{adventureToDelete.title}</strong>?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        This will delete:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText primary="All sessions and user progress" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="All POIs and workflow versions" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="All rewards, tickets, and tags" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="All media files (images, etc.)" />
                        </ListItem>
                        <ListItem>
                          <ListItemText primary="All discussions and comments" />
                        </ListItem>
                      </List>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {deleteResult ? (
                <Button onClick={handleDeleteDialogClose} variant="contained">
                  Close
                </Button>
              ) : (
                <>
                  <Button onClick={handleDeleteDialogClose} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteConfirm}
                    color="error"
                    variant="contained"
                    disabled={deleting}
                    startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
                  >
                    {deleting ? 'Deleting...' : 'Delete Adventure'}
                  </Button>
                </>
              )}
            </DialogActions>
          </Dialog>

          {/* Import Dialog */}
          <AdventureImportDialog
            open={importDialogOpen}
            onClose={() => setImportDialogOpen(false)}
            onComplete={() => {
              fetchAdventures()
              fetchStats()
            }}
          />
        </>
      )}

      {activeTab === 1 && (
        <CategoryListTable />
      )}
    </Box>
  )
}
